import { Project, WorkflowsService } from '@/api/';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useStore } from '@nanostores/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, PlusIcon } from 'lucide-react';
import { DeleteWorkflowConfirmation } from './DeleteWorkflowConfirmation';

/**
 * Runs the project and starts the generation.  We put this on a button
 * so that we can have some control later on so that the user can configure
 * some additional settings like column order and so on.
 * @param project The project to run.
 */
async function handleRunProject(project: Project) {
  await runProject(project.id);
}

async function handleCancelProject(project: Project) {
  await cancelProject(project.id);
}

export const WorkflowsListing = () => {
  const org = useStore($org);

  const ws = useStore($workspace);
  const queryClient = useQueryClient();
  const [showExecutionDetailsDrawer, setShowExecutionDetailsDrawer] = useState(false);

  // get workflows query
  const workflows = useQuery({
    queryKey: ['getWorkflows', ws?.workspace?.id],
    enabled: !!ws?.workspace?.id,
    queryFn: async () => {
      const res = await WorkflowsService.workflows(ws?.workspace?.id ?? '');
      return res;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const sortedWorkflows = workflows.data
    ?.slice()
    ?.sort(
      (a, b) =>
        new Date(a?.createdAtUtc).getTime() -
        new Date(a?.createdAtUtc).getTime(),
    );

  const [isDeleteWorkflowModalOpen, setIsDeleteWorkflowModalOpen] =
    useState(false);
  const [workflowToBeDeleted, setWorkflowToBeDeleted] = useState('');

  const formatDateString = (date: string | Date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are zero-based, so add 1
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };
  function getDateRangeString(
    startDate: string | Date,
    endDate: string | Date,
  ) {
    const startDateString = formatDateString(startDate);

    const endDateString = formatDateString(endDate);

    if (startDateString === endDateString) {
      return `for ${startDateString}`;
    } else {
      return `between ${startDateString} and ${endDateString}`;
    }
  }
  
  useEffect(() => {
    if (workflows?.isSuccess) {
      if (workflows?.data?.length > 0) {
        $workflows.set(workflows?.data);
      }
    }
  }, [ws]);
  return (
    <>
      {workflows?.isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <ul role="list" className="divide-y divide-border">
          {sortedWorkflows &&
            sortedWorkflows.map(workflow => {
              return (
                <li
                  key={workflow?.id}
                  className="flex items-center justify-between gap-x-6 py-5"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-x-3">
                      <p className="text-sm font-semibold leading-6">
                        {workflow?.name}
                      </p>

                      <Badge
                        variant="outline"
                        className={cn(
                          workflow?.isActive && 'bg-green-700 text-background',

                          !workflow?.isActive && 'bg-gray-700 text-background',

                        )}
                      >
                        {workflow?.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-muted-foreground">
                      <p className="whitespace-nowrap">
                        Created on{' '}
                        <time dateTime={workflow?.createdAtUtc}>
                          {new Date(
                            workflow?.createdAtUtc,
                          ).toLocaleDateString()}
                        </time>
                      </p>
                      <svg
                        viewBox="0 0 2 2"
                        className="h-0.5 w-0.5 fill-current"
                      >
                        <circle cx={1} cy={1} r={1} />
                      </svg>
                      <p className="truncate">
                        Created by {workflow?.createdBy?.email || 'Unknown'}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-none items-center gap-x-4">
                    <>
                      <Button
                        onClick={e => {
                          if (org && ws)
                            $router.open(
                              `/organization/${org.id}/workspace/${ws.workspace.id}/workflows/${workflow?.id}`,
                            );
                        }}
                      >
                        View Workflow
                      </Button>
                    </>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon">
                          <EllipsisVerticalIcon className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-56">
                        <DropdownMenuLabel>Options</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          // dsable the button if the workflow's last execution time is less than 1 hour from now
                          disabled={
                            workflow?.lastExecutionDateTime !== null &&
                            new Date(
                              workflow?.lastExecutionDateTime,
                            ).getTime() >
                              new Date().getTime() - 3600000
                          }
                          onClick={() => {
                            setWorkflowToBeDeleted(workflow.id);
                            setIsDeleteWorkflowModalOpen(true);
                          }}
                        >
                          Delete Workflow
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button onClick={() => setShowExecutionDetailsDrawer(true)}>
                      <PlusIcon className="mr-2 h-4 w-4" /> Workflow Runs
                     </Button>
                  </div>
                </li>
              );
            })}
        </ul>
      )}

      <DeleteWorkflowConfirmation
        isOpen={isDeleteWorkflowModalOpen}
        handleOpen={setIsDeleteWorkflowModalOpen}
        workflowId={workflowToBeDeleted}
      />
    </>
  );
};
